import {
    CameraAlt as CameraIcon,
    PhotoLibrary as GalleryIcon,
    CloudUpload as UploadIcon,
    Videocam as VideoIcon,
} from '@mui/icons-material';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Paper,
    Typography
} from '@mui/material';
import React, { useRef, useState } from 'react';

interface MobileUploadProps {
    onFileSelect: (file: File) => void;
    uploadProgress?: number;
    isUploading?: boolean;
    uploadStatus?: string;
}

const MobileUpload: React.FC<MobileUploadProps> = ({
    onFileSelect,
    uploadProgress = 0,
    isUploading = false,
    uploadStatus = '',
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const videoInputRef = useRef<HTMLInputElement>(null);
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const [dragOver, setDragOver] = useState(false);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            onFileSelect(file);
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(true);
    };

    const handleDragLeave = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragOver(false);
        
        const files = event.dataTransfer.files;
        if (files.length > 0) {
            onFileSelect(files[0]);
        }
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    return (
        <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
            <Typography variant="h4" component="h1" gutterBottom textAlign="center">
                Upload Your Memories
            </Typography>
            
            <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
                Share your photos and videos with the family
            </Typography>

            {/* Mobile-Optimized Upload Area */}
            <Paper
                sx={{
                    p: 4,
                    mb: 3,
                    border: '2px dashed',
                    borderColor: dragOver ? 'primary.main' : 'grey.300',
                    bgcolor: dragOver ? 'primary.50' : 'background.paper',
                    borderRadius: 2,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: 'primary.50',
                    },
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
            >
                <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                    Tap to select files or drag & drop
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Photos, videos, and documents welcome
                </Typography>
            </Paper>

            {/* Mobile-Specific Upload Buttons */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 3 }}>
                {/* Camera Capture */}
                <Card 
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => cameraInputRef.current?.click()}
                >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <CameraIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                        <Typography variant="h6" gutterBottom>
                            Take Photo/Video
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Use your camera
                        </Typography>
                    </CardContent>
                </Card>

                {/* Gallery Selection */}
                <Card 
                    sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                    onClick={() => videoInputRef.current?.click()}
                >
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                        <GalleryIcon sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
                        <Typography variant="h6" gutterBottom>
                            Choose from Library
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Select existing files
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Upload Progress */}
            {isUploading && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <VideoIcon color="primary" />
                            <Typography variant="h6" sx={{ flexGrow: 1 }}>
                                Uploading...
                            </Typography>
                            <Chip 
                                label={`${Math.round(uploadProgress)}%`} 
                                color="primary" 
                                size="small" 
                            />
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={uploadProgress} 
                            sx={{ mb: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                            {uploadStatus}
                        </Typography>
                    </CardContent>
                </Card>
            )}

            {/* Upload Tips for Mobile */}
            <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                    <strong>iPhone Tips:</strong>
                    <br />
                    • For best results, use Safari browser
                    <br />
                    • Large videos may take several minutes to upload
                    <br />
                    • Keep the app open during upload for best performance
                </Typography>
            </Alert>

            {/* Hidden File Inputs */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,.doc,.docx"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            
            {/* Camera Input - optimized for mobile */}
            <input
                ref={cameraInputRef}
                type="file"
                accept="image/*,video/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
            
            {/* Video/Photo Library Input */}
            <input
                ref={videoInputRef}
                type="file"
                accept="image/*,video/*"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
            />
        </Box>
    );
};

export default MobileUpload;
